/**
 * Checkpoint editor view settings (bleed, grid visibility, guides, snap to grid, etc.).
 *
 * GET /checkpoints/{spreadId}/editorsettings/viewsettings
 * PUT /checkpoints/{spreadId}/editorsettings/viewsettings/update
 */

import { httpClient } from '@/api/http/client'
import { logger } from '@/utils/logger'
import type { EditorGridMode } from '@/api/services/book-setting.service'

function enc(value: string): string {
  return encodeURIComponent(value.trim())
}

function viewSettingsPath(spreadId: string): string {
  return `/checkpoints/${enc(spreadId)}/editorsettings/viewsettings`
}

/** Matches `document_models.ViewSettings` JSON tags */
export interface ViewSettingsDto {
  showBleed: boolean
  showGrid: string
  showGuides: boolean
  showLink: boolean
  showSmartGuides: boolean
  showSticky: boolean
  snapToGrid: boolean
  /** Spread-level checkpoint settings updated timestamp. */
  updatedAt?: string
}

export const DEFAULT_VIEW_SETTINGS: ViewSettingsDto = {
  showBleed: true,
  showGrid: 'false',
  showGuides: true,
  showLink: false,
  showSmartGuides: true,
  showSticky: false,
  snapToGrid: false,
}

function normalizeShowGrid(raw: unknown): string {
  if (typeof raw === 'string') return raw
  if (typeof raw === 'boolean') return raw ? 'true' : 'false'
  if (raw != null) return String(raw)
  return 'false'
}

function normalizeUpdatedAt(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()

  return trimmed === '' ? undefined : trimmed
}

/** API `showGrid`: `"false"` | `"full"` | `"half"` (lowercase). */
export function normalizeShowGridForApi(raw: unknown): string {
  const s = normalizeShowGrid(raw).trim().toLowerCase()

  if (s === 'full' || s === 'half') return s

  return 'false'
}

/** Maps checkpoint `showGrid` to editor grid overlay mode. */
export function showGridStringToGridMode(showGrid: string): EditorGridMode {
  const s = showGrid.trim().toLowerCase()

  if (s === 'full') return 'full'
  if (s === 'half') return 'half'

  return 'off'
}

/** Maps editor grid mode to API `showGrid` string for PUT. */
export function gridModeToShowGridString(mode: EditorGridMode): string {
  if (mode === 'full') return 'full'
  if (mode === 'half') return 'half'
  return 'false'
}

function normalizeGetPayload(raw: unknown): ViewSettingsDto {
  if (raw == null || typeof raw !== 'object') {
    return { ...DEFAULT_VIEW_SETTINGS }
  }

  const o = raw as Record<string, unknown>

  return {
    showBleed: typeof o.showBleed === 'boolean' ? o.showBleed : DEFAULT_VIEW_SETTINGS.showBleed,
    showGrid: normalizeShowGridForApi(o.showGrid),
    showGuides: typeof o.showGuides === 'boolean' ? o.showGuides : DEFAULT_VIEW_SETTINGS.showGuides,
    showLink: typeof o.showLink === 'boolean' ? o.showLink : DEFAULT_VIEW_SETTINGS.showLink,
    showSmartGuides:
      typeof o.showSmartGuides === 'boolean'
        ? o.showSmartGuides
        : DEFAULT_VIEW_SETTINGS.showSmartGuides,
    showSticky: typeof o.showSticky === 'boolean' ? o.showSticky : DEFAULT_VIEW_SETTINGS.showSticky,
    snapToGrid: typeof o.snapToGrid === 'boolean' ? o.snapToGrid : DEFAULT_VIEW_SETTINGS.snapToGrid,
    updatedAt: normalizeUpdatedAt(o.updatedAt),
  }
}

/**
 * WMPCM-10576: a brand-new spread's blank template (served from the NSDS/Planbook API) predates
 * Larry's `<ViewSettings>` XML extension, so the backend has no explicit ViewSettings for it yet
 * — nil, not a stored `false`. The DTO shape alone can't tell the two apart (a nil record comes
 * back with every field defaulted), but a record that was actually saved always carries
 * `updatedAt`. Detect that and correct the one default that's wrong for a brand-new spread — Show
 * Text Linking should start on — leaving a genuinely saved `showLink: false` untouched. Returns
 * the same object reference when nothing needs correcting, so callers can tell whether the result
 * needs to be persisted back to the API.
 */
export function applyNewSpreadViewSettingsDefaults(settings: ViewSettingsDto): ViewSettingsDto {
  if (settings.updatedAt !== undefined) return settings

  return { ...settings, showLink: true }
}

async function getViewSettings(spreadId: string): Promise<ViewSettingsDto | null> {
  try {
    const raw = await httpClient.get<unknown>(viewSettingsPath(spreadId))

    return normalizeGetPayload(raw)
  } catch (error) {
    logger.warn(`Failed to load view settings for spread ${spreadId}:`, error)
    return null
  }
}

async function updateViewSettings(spreadId: string, body: ViewSettingsDto): Promise<void> {
  const payload: ViewSettingsDto = {
    showBleed: body.showBleed,
    showGrid: normalizeShowGridForApi(body.showGrid),
    showGuides: body.showGuides,
    showLink: body.showLink,
    showSmartGuides: body.showSmartGuides,
    showSticky: body.showSticky,
    snapToGrid: body.snapToGrid,
  }

  await httpClient.put<ViewSettingsDto, unknown>(`${viewSettingsPath(spreadId)}/update`, payload)
}

export const viewSettingsService = {
  getViewSettings,
  updateViewSettings,
}
